/**
 * PhaseTimingTracker — singleton coordinator for per-frame phase timings.
 *
 * Two capture sites in the latency-attribution arc fire at different times
 * within a single render frame (input keydown can be mid-frame; phase-2 sums
 * the EntitySystem accumulator loop). This tracker collates them into a single
 * PhaseTimingSample per frame, anchored on EntitySystem.update() as the
 * per-frame boundary.
 *
 * Capture API (called from the sites):
 *   noteKeydownAt(eventTimeStampMs)   ← InputSystem.handleNativeKeyDown
 *   noteCommandRead(performanceNow)   ← InputSystem.generateCommand (consumes
 *                                         the pending keydown timestamp)
 *   notePhase2(deltaMs)               ← EntitySystem.update accumulator wrap
 *   endFrame()                        ← EntitySystem.update bottom (always)
 *
 * Note: Phase 6 (reconcile) was removed 2026-05-27 — client-owned movement
 * has no reconciliation step.
 */

import { PhaseTimingBuffer, PhaseTimingSample } from './PhaseTimingBuffer';

class PhaseTimingTrackerImpl {
  private buffer = new PhaseTimingBuffer();

  // In-flight per-frame state. Cleared by endFrame().
  private pendingKeydownTs: number | null = null;
  private framePhase1Ms: number | null = null;
  private framePhase2Ms = 0;

  /**
   * Record the moment a raw browser keydown event fired. Use the
   * `KeyboardEvent.timeStamp` value (sub-ms precision, monotonic with
   * `performance.now()` since both share the document's time origin).
   * Subsequent calls overwrite earlier pending keydowns — phase-1
   * measures the most-recent-keydown latency, not all of them.
   */
  noteKeydownAt(eventTimeStampMs: number): void {
    if (Number.isFinite(eventTimeStampMs)) {
      this.pendingKeydownTs = eventTimeStampMs;
    }
  }

  /**
   * Record that InputSystem just consumed a pending keydown into a
   * UserCommand. The phase-1 delta is `now - pendingKeydownTs`. If no
   * keydown is pending (input frame had no new keys), phase1 stays null.
   */
  noteCommandRead(now: number = performance.now()): void {
    if (this.pendingKeydownTs !== null) {
      this.framePhase1Ms = Math.max(0, now - this.pendingKeydownTs);
      this.pendingKeydownTs = null;
    }
  }

  /**
   * Add to the running phase-2 (predict + sprite sync) wall-time total
   * for the current frame. Called from EntitySystem.update around each
   * physics sub-tick + the post-loop syncSpritePosition call.
   */
  notePhase2(deltaMs: number): void {
    if (Number.isFinite(deltaMs) && deltaMs > 0) {
      this.framePhase2Ms += deltaMs;
    }
  }

  /**
   * Push the assembled per-frame sample into the ring buffer and reset
   * in-flight state for the next frame. Called from EntitySystem.update
   * at frame end (after all phases for this frame have had their chance
   * to fire). Safe to call when no input/reconcile happened this frame —
   * those fields stay null in the sample.
   */
  endFrame(): void {
    const sample: PhaseTimingSample = {
      recordedAt: performance.now(),
      phase1Ms: this.framePhase1Ms,
      phase2Ms: this.framePhase2Ms,
    };
    this.buffer.push(sample);
    this.framePhase1Ms = null;
    this.framePhase2Ms = 0;
  }

  // ── Read API (consumed by multiplayerDiagnostics + DiagnosticHub) ────

  latest(): PhaseTimingSample | null {
    return this.buffer.latest();
  }

  rollingMean(phase: 'phase1Ms' | 'phase2Ms', windowSize?: number): number {
    return this.buffer.rollingMean(phase, windowSize);
  }

  rollingMax(phase: 'phase1Ms' | 'phase2Ms', windowSize?: number): number {
    return this.buffer.rollingMax(phase, windowSize);
  }

  size(): number {
    return this.buffer.size();
  }

  reset(): void {
    this.buffer.reset();
    this.pendingKeydownTs = null;
    this.framePhase1Ms = null;
    this.framePhase2Ms = 0;
  }
}

export const PhaseTimingTracker = new PhaseTimingTrackerImpl();
